import './PackageList.scss';
import PropTypes from 'prop-types';
import Package from '../Package/Package';
import {
  faCheck,
  faCube,
  faCubes,
  faForward,
  faListNumeric,
  faPrint,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import Modal from '../Modal/Modal';
import EditPackageType from '../EditPackageType/EditPackageType';
import EditPackageHazards from '../EditPackageHazards/EditPackageHazards';
import EditPackageDetails from '../EditPackageDetails/EditPackageDetails';
import Checkbox from '../Checkbox/Checkbox';
import EditBay from '../EditBay/EditBay';
import PrintLabel from '../PrintLabel/PrintLabel';
import ManualCompletion from '../ManualCompletion/ManualCompletion';
import UndoManualCompletion from '../ManualCompletion/UndoManualCompletion';

const PackageList = ({
  type,
  packages,
  job,
  refetchPackages,
  bays,
  racks,
  disableMassAssign,
  specialActionType,
  specialAction,
  specialCompletedAction,
  numGhostPackages,
  ghostAction,
  massSpecial,
  completedPackages,
}) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState([]);

  const [modalOpen, setModalOpen] = useState();

  const openModal = (type, packageName, item) => {
    setFormDisabled(true);
    if ((packageName && item) || selectedPackages.length > 0) {
      setModalOpen({ type: type, packageName: packageName, package: item });
    } else {
      setModalOpen({ type: 'none_selected' });
    }
    setTimeout(() => {
      setFormDisabled(false);
    }, 250);
  };

  const getModalTitle = () => {
    switch (modalOpen.type) {
      case 'manually_complete':
        return `Mark ${modalOpen.packageName} as gone`;
      case 'details':
        return 'Edit Details';
      case 'hazard':
        return 'Edit Hazards';
      case 'package':
        return 'Edit Package Type';
      case 'bay':
        return 'Select Bay';
      case 'bay_assign':
        return 'Select Bay';
      case 'print':
        return 'Select a printer';
      default:
        return null;
    }
  };

  const getModalType = () => {
    switch (modalOpen.type) {
      case 'complete_packages':
        return (
          <UndoManualCompletion
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            pkg={{ packageName: modalOpen.packageName, package: modalOpen.package }}
          />
        );
      case 'manually_complete':
        return (
          <ManualCompletion
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            pkg={{ packageName: modalOpen.packageName, package: modalOpen.package }}
          />
        );
      case 'details':
        return (
          <EditPackageDetails
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            packages={
              modalOpen.packageName
                ? [{ packageName: modalOpen.packageName, package: modalOpen.package }]
                : selectedPackages
            }
          />
        );
      case 'hazard':
        return (
          <EditPackageHazards
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            packages={
              modalOpen.packageName
                ? [{ packageName: modalOpen.packageName, package: modalOpen.package }]
                : selectedPackages
            }
          />
        );
      case 'package':
        return (
          <EditPackageType
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            packages={
              modalOpen.packageName
                ? [{ packageName: modalOpen.packageName, package: modalOpen.package }]
                : selectedPackages
            }
          />
        );
      case 'bay':
        return (
          <EditBay
            bays={bays}
            racks={racks}
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            packages={
              modalOpen.packageName
                ? [{ packageName: modalOpen.packageName, package: modalOpen.package }]
                : selectedPackages
            }
          />
        );
      case 'bay_assign':
        return (
          <EditBay
            bays={bays}
            racks={racks}
            setModalOpen={setModalOpen}
            refetchPackages={refetchPackages}
            packages={[{ packageName: modalOpen.packageName, package: modalOpen.package }]}
          />
        );
      case 'print':
        return <PrintLabel setModalOpen={setModalOpen} packages={selectedPackages} />;
      default:
        return (
          <div className="none_selected">
            <div className="message">
              Please select one or more packages.
              <div className="close_button" onClick={() => setModalOpen(false)}>
                ×
              </div>
            </div>
            <div className="ok_button" onClick={() => setModalOpen(false)}>
              OK
            </div>
          </div>
        );
    }
  };

  const selectAll = () => {
    if (selectedPackages.length == packages.length) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages(
        packages.map(item => ({
          packageName: `${item?.job?.jobReference}-${item?.packageNumber}`,
          package: item,
        })),
      );
    }
  };

  const toggleSelected = (packageName, item) => {
    if (selectedPackages.some(selectedItem => selectedItem.package.id === item.id)) {
      const updatedPackages = selectedPackages.filter(selectedItem => {
        return selectedItem.package.id !== item.id;
      });
      setSelectedPackages(updatedPackages);
    } else {
      setSelectedPackages([...selectedPackages, { packageName: packageName, package: item }]);
    }
  };

  useEffect(() => {
    setSelectedPackages(
      packages?.map(item => ({
        packageName: `${item?.job?.jobReference}-${item?.packageNumber}`,
        package: item,
      })),
    );
  }, [packages]);

  return (
    <>
      <div className="package_list_container">
        {type == 'unmeasured' && (
          <div className="job_title">
            {job} <span>{packages.length} unmeasured packages</span>
          </div>
        )}
        {!disableMassAssign && (
          <div className={`mass_assign_header`}>
            <div className={`mass_checkbox`} onClick={() => selectAll()} data-testid="select_all">
              <FontAwesomeIcon icon={faCheck} className="green tick" />
              /
              <FontAwesomeIcon icon={faCheck} className="tick" />
            </div>
            <div
              className={`mass_assign_details ${formDisabled && 'disabled'}`}
              onClick={() => openModal('details')}
            >
              Mass-assign details
            </div>
            <div
              className={`mass_assign_package ${formDisabled && 'disabled'}`}
              onClick={() => openModal('package')}
              data-testid="mass_assign_package"
            >
              <FontAwesomeIcon icon={faCubes} className="cubes" />
            </div>
            <div
              className={`mass_assign_hazard ${
                (formDisabled || !packages[0]?.job?.cargoHazardous) && 'disabled'
              }`}
              onClick={() => {
                if (!formDisabled && packages[0]?.job?.cargoHazardous) openModal('hazard');
              }}
            >
              {packages[0]?.job?.cargoHazardous ? 'Mass-assign DGs' : 'No DGs'}
            </div>
            <div
              className={`mass_assign_special ${formDisabled && 'disabled'} ${
                massSpecial && 'update_number_unloaded'
              }`}
              onClick={massSpecial ? () => massSpecial() : () => openModal('bay')}
            >
              {massSpecial ? (
                <FontAwesomeIcon icon={faListNumeric} data-testid="update_num_packages_icon" />
              ) : (
                'Bay'
              )}
            </div>
          </div>
        )}
        {packages &&
          packages.map((item, i) => {
            return (
              <div className="package_container" key={`container_${i}`}>
                {disableMassAssign ? (
                  <div className="checkbox_replacement">
                    {specialActionType == 'complete_packages' ? (
                      <FontAwesomeIcon icon={faForward} className="fast_forward_icon" />
                    ) : (
                      <FontAwesomeIcon icon={faCube} className="package_icon" />
                    )}
                  </div>
                ) : (
                  <Checkbox
                    selected={selectedPackages.some(
                      selectedItem => selectedItem.package.id === item.id,
                    )}
                    handleClick={() =>
                      toggleSelected(`${item.job?.jobReference}-${item.packageNumber}`, item)
                    }
                    label={`${job}_${i}`}
                  />
                )}
                <Package
                  disableButtons={specialActionType == 'complete_packages'}
                  individualPackage={item}
                  key={`package_${i}`}
                  openModal={openModal}
                  bays={bays}
                  racks={racks}
                  specialActionType={
                    specialActionType
                      ? specialActionType == 'complete_packages'
                        ? 'undo_manually_complete'
                        : specialActionType
                      : 'bay'
                  }
                  specialAction={id => {
                    specialAction
                      ? specialAction(id)
                      : openModal(
                          specialActionType ? specialActionType : 'bay',
                          `${item.job?.jobReference}-${item.packageNumber}`,
                          item,
                        );
                  }}
                />
              </div>
            );
          })}
        {numGhostPackages > 0 &&
          Array(numGhostPackages)
            .fill()
            .map((item, i) => {
              const pkg = {
                job: job,
                packageType: {},
                packageNumber:
                  packages.length > 0 ? packages[packages.length - 1].packageNumber + i + 1 : i + 1,
              };
              return (
                <div className="package_container" key={`ghost_container_${i}`}>
                  <div className="checkbox_replacement" />
                  <Package
                    data-testid={`ghost_package_${i}`}
                    disableButtons
                    individualPackage={pkg}
                    key={`ghost_package_${i}`}
                    openModal={openModal}
                    bays={bays}
                    racks={racks}
                    specialActionType={'add_unit'}
                    specialAction={id => ghostAction(id)}
                  />
                </div>
              );
            })}
        {completedPackages?.length > 0 &&
          completedPackages?.map((completedPackage, i) => {
            return (
              <div className="package_container" key={`complete_container_${i}`}>
                <div className="checkbox_replacement">
                  <FontAwesomeIcon icon={faTruck} className="truck_icon" />
                </div>
                <Package
                  data-testid={`complete_package_${i}`}
                  disableButtons
                  individualPackage={completedPackage}
                  key={`complete_package_${i}`}
                  openModal={openModal}
                  bays={bays}
                  racks={racks}
                  specialActionType={'go_to_delivery'}
                  specialAction={specialCompletedAction}
                />
              </div>
            );
          })}
        {!disableMassAssign && (
          <div className="print_label_row">
            <div className="block" />
            <div className="print_button" onClick={() => openModal('print')}>
              <FontAwesomeIcon icon={faPrint} className="printer_icon" />
              Print labels for the selected packages
            </div>
            <div className="block" />
          </div>
        )}
      </div>
      {modalOpen && (
        <Modal title={getModalTitle()} content={getModalType()} setModalOpen={setModalOpen} />
      )}
    </>
  );
};

PackageList.propTypes = {
  packages: PropTypes.array,
  job: PropTypes.string,
  refetchPackages: PropTypes.func,
  bays: PropTypes.array,
  racks: PropTypes.object,
  disableMassAssign: PropTypes.bool,
  type: PropTypes.string,
  specialActionType: PropTypes.string,
  specialAction: PropTypes.func,
  numGhostPackages: PropTypes.number,
  ghostAction: PropTypes.func,
  massSpecial: PropTypes.func,
  completedPackages: PropTypes.array,
  specialCompletedAction: PropTypes.func,
};

export default PackageList;
